// eslint-disable-next-line
import { MRT_ColumnDef } from 'material-react-table'
import { useMemo } from 'react'
import { useGetAssetGroupByIdQuery } from '../../app/redux-fetch/apiAssets'
import { useGetJourneyLoadsByIdQuery } from '../../app/redux-fetch/apiOrders'
import MRTDataGridv2 from '../../components/MRTDataGrid/MRTDataGridv2'
import { JourneyLoadDto } from '../Journey/types'
import { TransportJob } from './types'

interface TransportJobLoadsProps {
  tenantId: number
  transportJob: TransportJob
}

// A job's loads are allocated to its assets in ASSETS (AssetLoadAllocation);
// the load details come from the journey's loads in ORDERS.
const TransportJobLoads = ({ tenantId, transportJob }: TransportJobLoadsProps) => {
  const journeyId = transportJob.route?.leg?.journeyId

  const {
    data: assetGroup,
    isLoading: isAssetGroupLoading,
    isError: isAssetGroupError,
  } = useGetAssetGroupByIdQuery({
    tenantId,
    assetGroupId: transportJob.assetGroupId,
  })
  const {
    data: journeyLoads = [],
    isLoading: isJourneyLoadsLoading,
    isError: isJourneyLoadsError,
  } = useGetJourneyLoadsByIdQuery(
    { tenantId, journeyId: journeyId ?? -1 },
    { skip: !journeyId }
  )
  const isLoading = isAssetGroupLoading || isJourneyLoadsLoading

  const loads = useMemo(() => {
    const loadIds = new Set(
      (assetGroup?.assetAllocations ?? [])
        .flatMap((assetAllocation) => assetAllocation.loadAllocations ?? [])
        .map((loadAllocation) => loadAllocation.loadId)
    )
    // The endpoint is typed as IJourneyLegLoad[] but returns JourneyLoadDto[]
    return (journeyLoads as unknown as JourneyLoadDto[]).filter((load) => loadIds.has(load.loadId))
  }, [assetGroup, journeyLoads])

  // eslint-disable-next-line
  const columns = useMemo<Array<MRT_ColumnDef<JourneyLoadDto>>>(
    () => [
      {
        header: 'Quantity',
        accessorFn: (row) => row.quantity,
      },
      {
        header: 'Description',
        accessorFn: (row) => row.description ?? '',
      },
      {
        header: 'Tracking Number',
        accessorFn: (row) => row.trackingNumber ?? '',
      },
      {
        header: 'Customer',
        accessorFn: (row) => row.customerName ?? '',
      },
    ],
    []
  )

  if (isAssetGroupError || isJourneyLoadsError) {
    return <>Could not load the loads for this Transport Job</>
  }

  if (!isLoading && loads.length === 0) {
    return <>No Loads in the Transport Job</>
  }

  return (
    <MRTDataGridv2
      state={{ isLoading }}
      columns={columns}
      data={loads}
      getRowId={(row) => String(row.loadId)}
      enableColumnActions={false}
      enableColumnFilters={false}
      enablePagination={false}
      enableSorting={false}
      enableBottomToolbar={false}
      enableTopToolbar={false}
    />
  )
}

export default TransportJobLoads
